import React, { FC } from "react";
import classNames from "classnames";
import {
  childrenModeIcon,
  childrenModeIconCrossed,
} from "../../../../assets/icons";
import { TerminalAppearanceResponse } from "../../../../types/Admin/TerminalAppearanceTypes";
import {
  getReadableTextColor,
  getSpacePlanetHueRotate,
  ResolvedChildrenModeVisualSettings,
} from "../../../../utils/Terminal/childrenModeThemes";
import classes from "../Normal/NormalMode/NormalMode.module.scss";
import {
  checkIsCategory,
  ChildrenModeIconMeta,
  getChildrenModeIconMeta,
  getChildrenTicketCircleStyle,
  getCitoCardStyle,
  getTicketName,
  isCitoTile,
  languageType,
  TerminalTicketItem,
} from "../Normal/NormalMode/terminalModeShared";
import {
  getChildrenModeThemeComponent,
  getChildrenModeThemeGreetingComponent,
  isPresetChildrenTheme,
} from "./childrenModeThemeRegistry";
import { elementsLocalization } from "../Normal/NormalMode/terminalModeShared";

type ChildrenModeProps = {
  language: languageType;
  currentSectionsServices: TerminalTicketItem[];
  currentIndex: number;
  activeModal: boolean;
  isLoadingGetTicket: boolean;
  activeTicketId?: number;
  appearance?: TerminalAppearanceResponse;
  childrenVisual: ResolvedChildrenModeVisualSettings;
  presetThemeId?: string | null;
  childrenTileColors: Map<string, string>;
  useCategoryGrid: boolean;
  showChildrenModeButton: boolean;
  hideChildrenModeToggle: boolean;
  showGreeting: boolean;
  greetingTitle: string;
  greetingSubTitle: string;
  onBack: () => void;
  onToggleChildrenMode: () => void;
  onCategoryClick: (item: TerminalTicketItem) => void;
};

export const ChildrenMode: FC<ChildrenModeProps> = ({
  language,
  currentSectionsServices,
  currentIndex,
  activeModal,
  isLoadingGetTicket,
  activeTicketId,
  appearance,
  childrenVisual,
  presetThemeId,
  childrenTileColors,
  useCategoryGrid,
  showChildrenModeButton,
  hideChildrenModeToggle,
  showGreeting,
  greetingTitle,
  greetingSubTitle,
  onBack,
  onToggleChildrenMode,
  onCategoryClick,
}) => {
  const isIndividualTheme = childrenVisual.isIndividualTheme;
  const ThemeCard = getChildrenModeThemeComponent(
    presetThemeId,
    isIndividualTheme,
  );
  const ThemeGreeting = getChildrenModeThemeGreetingComponent(
    presetThemeId,
    isIndividualTheme,
  );
  const presetTheme =
    !isIndividualTheme && isPresetChildrenTheme(presetThemeId);
  const childrenButtonShape = childrenVisual.buttonShape;

  return (
    <div className={classes.terminalModeContent}>
      {(currentIndex !== 0 || showGreeting) && (
        <>
          {currentIndex !== 0 && (
            <div className={classes.chooseServiceText}>
              <button onClick={onBack} className={classes.buttonBack}>
                <div className={classes.buttonBackArrow} />
                <span>{elementsLocalization.returnBtn[language]}</span>
              </button>
            </div>
          )}
          {currentIndex === 0 && showGreeting && ThemeGreeting && (
            <ThemeGreeting title={greetingTitle} subTitle={greetingSubTitle} />
          )}
          {currentIndex === 0 && showGreeting && !ThemeGreeting && (
            <div className={classes.chooseServiceText}>
              <div>
                <p>{greetingTitle}</p>
                <p>{greetingSubTitle}</p>
              </div>
            </div>
          )}
        </>
      )}

      {!hideChildrenModeToggle && showChildrenModeButton && (
        <button
          type="button"
          className={classes.childrenModeHomeButton}
          onClick={onToggleChildrenMode}
          aria-label="Обычный режим"
        >
          <img src={childrenModeIconCrossed} alt="" />
          <span>Обычный режим</span>
        </button>
      )}

      <div
        className={classNames(classes.ticketList, {
          [classes.ticketListLargeIcons]: currentSectionsServices.length <= 3,
          [classes.ticketListSmallIcons]: currentSectionsServices.length > 3,
          [classes.isBackBtnActive]: currentIndex !== 0,
          [classes.ticketListCategoryGrid]: useCategoryGrid,
        })}
      >
        {currentSectionsServices.length > 0 &&
          currentSectionsServices.map(
            (ticket: TerminalTicketItem, index: number) => {
              const ticketName = getTicketName(ticket, language);
              const isBtnDisabled =
                (!ticket.isActiveNow && !checkIsCategory(ticket)) ||
                (isLoadingGetTicket && activeTicketId === ticket.id) ||
                activeModal;
              const isCategory = checkIsCategory(ticket);
              const isService = !isCategory;
              const hideIcons = childrenVisual.hideIcons;
              const useStandardIcons =
                childrenVisual.showStandardModeIcons && !presetTheme;
              const childrenTileColor = childrenTileColors.get(
                String(ticket.id),
              );
              const childrenTileTextColor =
                childrenTileColor != null
                  ? getReadableTextColor(
                      childrenTileColor,
                      childrenVisual.buttonTextColor ?? "#FFFFFF",
                    )
                  : undefined;
              const childrenIconMeta = getChildrenModeIconMeta(
                ticket,
                useStandardIcons,
                hideIcons,
              );
              const ticketCircleStyle = getChildrenTicketCircleStyle(
                ticket,
                isCategory,
                useStandardIcons,
                hideIcons,
              );

              return (
                <button
                  className={classNames(classes.ticketCardContainer)}
                  key={`${ticket.id}_${index}`}
                  onClick={() => onCategoryClick(ticket)}
                  disabled={isBtnDisabled}
                  style={
                    isCitoTile(ticket) ? getCitoCardStyle(appearance) : undefined
                  }
                >
                  <ThemeCard
                    isCategory={isCategory}
                    isService={isService}
                    isActiveNow={Boolean(ticket.isActiveNow)}
                    shouldHideOuterCircle={hideIcons}
                    ticketCircleStyle={ticketCircleStyle}
                    ticketName={ticketName}
                    isChildrenMode
                    childrenButtonShape={childrenButtonShape}
                    childrenIconMeta={
                      childrenIconMeta as ChildrenModeIconMeta | null
                    }
                    childrenTileColor={childrenTileColor}
                    childrenTileTextColor={childrenTileTextColor}
                    planetHueRotate={
                      presetTheme ? getSpacePlanetHueRotate(index) : undefined
                    }
                  />
                </button>
              );
            },
          )}
      </div>
    </div>
  );
};

export const ChildrenModeToggleToChildren: FC<{
  show: boolean;
  onToggle: () => void;
}> = ({ show, onToggle }) => {
  if (!show) return null;

  return (
    <button
      type="button"
      className={classes.childrenModeHomeButton}
      onClick={onToggle}
      aria-label="Детский режим"
    >
      <img src={childrenModeIcon} alt="" />
      <span>Детский режим</span>
    </button>
  );
};
